import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import RelatedInventory from '../components/RelatedInventory';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';

const { FiAward, FiUsers, FiHeart, FiTrendingUp, FiMapPin, FiPhone, FiMail } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: 'Customer First',
      description: 'We put our customers at the center of everything we do, ensuring exceptional service and satisfaction.',
    },
    {
      icon: FiAward,
      title: 'Quality Assurance',
      description: 'Every vehicle undergoes rigorous inspection to meet our high standards for quality and reliability.',
    },
    {
      icon: FiUsers,
      title: 'Expert Team',
      description: 'Our experienced professionals are dedicated to helping you find the perfect vehicle for your needs.',
    },
    {
      icon: FiTrendingUp,
      title: 'Continuous Growth',
      description: 'We continuously evolve and improve our services to better serve our community.',
    },
  ];

  const team = [
    {
      name: 'Wayne Wilder',
      position: 'Owner, General Manager',
      image: '/assets/assets/images/team/wayne.webp',
      description: 'Founder of Paradise-Performance with expertise in quality vehicle sales and dealership management.',
    },
    {
      name: 'David Barrera',
      position: 'Sales Manager',
      image: '/assets/assets/images/team/david.webp',
      description: 'University of Texas Austin graduate bringing expertise in sales management and customer relations to help you find the perfect vehicle.',
    },
    {
      name: 'Colton Wilder',
      position: 'Lead Mechanic & Performance-Racing Technician',
      image: '/assets/assets/images/team/colton.webp',
      description: 'Expert in European car repair and diesel performance modifications with specialized training.',
    }
  ];

  return (

    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-black text-white"
    >
      <Helmet>
        <title>About Paradise-Performance | Family-Owned Faith Based Automotive Hub</title>
        <meta name="description" content="Meet our team — a faith-based, family-owned auto dealer in Paradise, TX. Serving DFW with affordable pricing, honest work and quality services since day one." />
        <link rel="canonical" href="https://paradise-performance.com/about" />
        <meta property="og:title" content="About Paradise-Performance | Family-Owned & Faith-Based Automotive Hub" />
        <meta property="og:description" content="Trusted auto dealer serving Paradise, TX with quality vehicles and honest service." />
        <meta property="og:url" content="https://paradise-performance.com/about" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Paradise-Performance",
            "url": "https://paradise-performance.com/about",
            "mainEntity": {
              "@type": "AutoDealer",
              "name": "Paradise-Performance",
              "foundingDate": "2023",
              "description": "Family-owned auto dealership specializing in quality used vehicles",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "305 Co Rd 3470",
                "addressLocality": "Paradise",
                "addressRegion": "TX",
                "postalCode": "76073"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <PageHero
        eyebrow="Our Story"
        title="The Journey to Paradise-Performance"
        subtitle="A faith-based family business committed to integrity, quality service, and building lasting relationships with our customers."
      />

      {/* Our Story */}
      <section className="bg-black py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-red-500 sm:text-sm">
                Built On Integrity
              </p>
              <motion.h2
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6 }}
                className="text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl"
              >
                Routine Auto Maintenance
              </motion.h2>
              <div className="mt-6 h-[3px] w-20 rounded-full bg-red-600" />
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-8 space-y-5 text-base leading-relaxed text-gray-400 md:text-lg"
              >
                <p>
                  Wayne Wilder founded Paradise-Performance after 27 years in corporate sales, asking "what is important to my family and me?" The business grew from a father-son hobby to a full-service dealership and repair shop focused on integrity and quality.
                </p>
                <p>
                  Our approach is simple: we sell cars and put our own families in. No salvage titles, no hidden problems, no pressure tactics. Each vehicle comes with a complete inspection report and service history. If something needs attention, we fix it before it hits the lot or tell you exactly what to expect.
                </p>
                <p>
                  Building trust isn't just good business – it's the right thing to do. Whether you need reliable transportation for work or a safe vehicle for your teenager, we'll help you find the right fit for your budget and needs. And unlike those big dealerships, we'll still wave when we see you around town.
                </p>
              </motion.div>
            </div>
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/50">
                <img
                  src="/assets/assets/images/business-shop-sign.webp"
                  alt="Paradise-Performance Dealership"
                  className="w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -right-3 rounded-2xl border border-white/10 bg-red-600 p-4 shadow-xl sm:-right-5">
                <div className="text-2xl font-bold">⭐️⭐️⭐️⭐️⭐️</div>
                <div className="text-sm font-medium text-white">Best Rated DFW</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="bg-neutral-950 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="What We Stand For"
            title="Faith-Based Used Car Dealership"
            subtitle="At Paradise-Performance, we are a faith-based family owned business dedicated to providing high-quality used vehicles, European & domestic repair serving Dallas, Denton & Fort Worth (or from anywhere else) drivers - Stop by today! We're not your typical used car lot."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: index * 0.08, duration: 0.5 }}
                className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 text-center transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]"
              >
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-red-600/20 bg-red-600/10 transition-colors group-hover:bg-red-600/20">
                  <SafeIcon icon={value.icon} className="h-7 w-7 text-red-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{value.title}</h3>
                <p className="text-sm leading-relaxed text-gray-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-black py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Faith & Family"
            title="Operating Our Business With Integrity"
            subtitle="Our business is founded on faith and family values. We operate with the understanding that integrity matters in every transaction and service we provide."
          />
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-5 text-base leading-relaxed text-gray-400 md:text-lg"
            >
              <p className="border-l-2 border-red-600 pl-6 text-lg italic text-gray-300 md:text-xl">
                "My wife Robin has always stood by me. Apart from Jesus, she's my rock. We share everything when it comes to our faith, our ideas, and the type of life we want for our family." - Wayne Wilder
              </p>
              <p>
                Our business runs on a simple principle: treat others the way we'd want to be treated. Sounds basic, but you'd be surprised how rare that is in the auto industry. My wife Robin and I didn't set out to revolutionize car repair. We just wanted to create the kind of shop we'd been looking for ourselves - one where you're not just another invoice number, where someone actually listens to that weird noise your car's been making, and where "I don't know, let me find out" is an acceptable answer.
              </p>
              <p>
                Every morning, our team starts with the same commitment: do right by our customers, even when nobody's watching. That means calling you if we find something unexpected instead of just fixing it and surprising you with the bill. It means suggesting the repair that makes sense for your situation, not just the one with the highest margin. Sometimes it even means telling you to wait on a repair if it's not critical yet.
              </p>
            </motion.div>
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/50">
                <img
                  src="/assets/assets/images/IMG_4237.webp"
                  alt="Engine work in progress in the Paradise-Performance service bay"
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-neutral-950 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The People Behind The Shop"
            title="Meet Our Team"
            subtitle="Our family team is committed to providing you with honest service and finding the right vehicle for your needs."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: index * 0.08, duration: 0.5 }}
                className="group overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03] transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]"
              >
                <div className="overflow-hidden bg-neutral-900">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="h-56 w-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="mb-1 text-xl font-semibold text-white">{member.name}</h3>
                  <div className="mb-3 text-sm font-medium uppercase tracking-wider text-red-500">{member.position}</div>
                  <p className="text-sm leading-relaxed text-gray-400">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment to Excellence */}
      <section className="bg-black py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-red-600/15 via-white/[0.03] to-white/[0.03] p-8 md:p-12"
          >
            <h2 className="mb-6 text-3xl font-bold tracking-tight text-white md:text-4xl">Commitment To Excellence</h2>
            <div className="space-y-5 text-base leading-relaxed text-gray-300 md:text-lg">
              <p>
                We're serious about having the industry's best service record. Unlike big dealers, we don't have a huge advertising budget. For us, it's about the details nobody sees - the extra time spent diagnosing an intermittent problem, the care we take with your car's running smoothly, the willingness to explain what we're doing and why.
              </p>
              <p>
                What sets us apart is consistency. Whether you're here for a simple oil change or a complex diesel repair, you get the same level of attention and expertise. We invest in ongoing training but avoid vehicles losing more sophistication. We maintain relationships with parts suppliers because our commitment is to quality. Most importantly, we remember that behind every car is a person counting on us to keep them safe and mobile.
              </p>
              <p>
                We believe in building behind our work which is why we offer solid warranties and actually honor the simple things you ask. It's an ordinary process for decades now, but when you combine our small town values, old-fashioned integrity, and modern expertise makes a pretty good combination.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-neutral-950 py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-white md:text-4xl">Ready to Experience the Paradise-Performance Difference?</h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-400">
            Visit us today and discover why families throughout North Texas have trusted us with their automotive needs for over 1 years.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="/inventory"
              className="inline-flex items-center justify-center rounded-xl bg-red-600 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-red-700"
            >
              Visit Our Showroom
            </a>
            <a
              href="tel:+18173822316"
              className="inline-flex items-center justify-center rounded-xl border border-white/30 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-white hover:text-black"
            >
              Call Us Today
            </a>
          </div>
        </div>
      </section>

      {/* Vehicle Inventory Links */}
      <section className="bg-black pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-white/10 bg-white p-6 shadow-2xl shadow-black/40 sm:p-8 md:p-10">
            <RelatedInventory limit={4} title="Vehicles For Sale" />
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;